(() => {
  const validatedForms = new WeakSet();
  const validatingForms = new WeakSet();

  /**
   * Get Shopify cart
   */
  async function getCart() {
    const root = window.Shopify?.routes?.root || "/";

    const response = await fetch(`${root}cart.js`, {
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Could not load cart");
    }

    return response.json();
  }

  /**
   * Check whether this is an Add To Cart form
   */
  function isAddToCartForm(form) {
    if (!(form instanceof HTMLFormElement)) {
      return false;
    }

    try {
      const action = new URL(
        form.action,
        window.location.origin
      );

      return action.pathname.includes("/cart/add");
    } catch {
      return false;
    }
  }

  /**
   * Show validation message
   */
  function showError(form, message) {
    let errorElement = form.querySelector(
      "[data-quantity-rule-error]"
    );

    if (!errorElement) {
      errorElement = document.createElement("div");

      errorElement.setAttribute(
        "data-quantity-rule-error",
        ""
      );

      errorElement.setAttribute("role", "alert");

      errorElement.style.marginTop = "10px";
      errorElement.style.fontSize = "14px";

      const submitButton = form.querySelector(
        '[type="submit"]'
      );

      if (submitButton) {
        submitButton.insertAdjacentElement(
          "afterend",
          errorElement
        );
      } else {
        form.appendChild(errorElement);
      }
    }

    errorElement.textContent = message;
    errorElement.hidden = false;
  }

  /**
   * Remove previous validation message
   */
  function clearError(form) {
    const errorElement = form.querySelector(
      "[data-quantity-rule-error]"
    );

    if (!errorElement) return;

    errorElement.textContent = "";
    errorElement.hidden = true;
  }

  /**
   * Validate quantity against min/max/step
   */
  function validateQuantity({
    existingQuantity,
    requestedQuantity,
    min,
    max,
    step,
  }) {
    const finalQuantity =
      existingQuantity + requestedQuantity;

    /*
     * Example:
     *
     * min = 2
     * step = 4
     *
     * valid:
     * 2, 6, 10, 14...
     */

    if (finalQuantity < min) {
      return {
        valid: false,
        message: `Minimum quantity is ${min} per order.`,
      };
    }

    if (finalQuantity > max) {
      const remaining = Math.max(
        0,
        max - existingQuantity
      );

      if (existingQuantity > 0) {
        return {
          valid: false,
          message:
            remaining > 0
              ? `Maximum quantity is ${max} per order. You already have ${existingQuantity} in your cart, so you can add up to ${remaining} more.`
              : `Maximum quantity is ${max} per order. You already have ${existingQuantity} in your cart.`,
        };
      }

      return {
        valid: false,
        message: `Maximum quantity is ${max} per order.`,
      };
    }

    /*
     * Same calculation as your backend:
     *
     * (quantity - min) % step === 0
     */
    if ((finalQuantity - min) % step !== 0) {
      return {
        valid: false,
        message:
          min !== step
            ? `Quantity must increase in steps of ${step}, starting from ${min}.`
            : `Quantity must be in multiples of ${step}.`,
      };
    }

    return {
      valid: true,
      finalQuantity,
    };
  }

  /**
   * Intercept ATC
   */
  async function handleSubmit(event) {
    const form = event.target;

    if (!isAddToCartForm(form)) {
      return;
    }

    /*
     * We already validated this submission.
     *
     * Allow the theme's original AJAX code to continue.
     */
    if (validatedForms.has(form)) {
      validatedForms.delete(form);
      return;
    }

    const quantityInput =
      form.querySelector(
        '[data-quantity-input][name="quantity"]'
      ) ||
      form.querySelector('[name="quantity"]');

    /*
     * No quantity selector?
     * Don't interfere with the theme.
     */
    if (!quantityInput) {
      return;
    }

    const variantInput =
      form.querySelector('[name="id"]');

    if (!variantInput) {
      return;
    }

    const variantId = Number(variantInput.value);

    if (!variantId) {
      return;
    }

    /*
     * Prevent double clicking while cart is loading
     */
    if (validatingForms.has(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    /*
     * Stop theme ATC temporarily
     */
    event.preventDefault();
    event.stopImmediatePropagation();

    validatingForms.add(form);

    clearError(form);

    try {
      /*
       * Read rules directly from your Liquid input
       */
      const requestedQuantity =
        Number(quantityInput.value) || 1;

      const min =
        Number(quantityInput.min) || 1;

      const step =
        Number(quantityInput.step) || 1;

      const max =
        quantityInput.max !== ""
          ? Number(quantityInput.max)
          : Infinity;

      /*
       * Basic quantity check
       */
      if (
        !Number.isInteger(requestedQuantity) ||
        requestedQuantity <= 0
      ) {
        showError(
          form,
          "Please enter a valid quantity."
        );

        return;
      }

      /*
       * Fetch current cart
       */
      const cart = await getCart();

      /*
       * Find ALL cart lines using this variant.
       *
       * Shopify can technically have the same
       * variant across multiple cart lines if
       * properties / selling plans differ.
       */
      const existingQuantity = cart.items
        .filter(
          (item) =>
            Number(item.variant_id) === variantId
        )
        .reduce(
          (total, item) =>
            total + Number(item.quantity || 0),
          0
        );

      /*
       * Validate FINAL quantity:
       *
       * cart quantity + quantity being added
       */
      const result = validateQuantity({
        existingQuantity,
        requestedQuantity,
        min,
        max,
        step,
      });

      if (!result.valid) {
        showError(form, result.message);
        return;
      }

      /*
       * Mark the next submit as validated.
       */
      validatedForms.add(form);

      /*
       * Re-submit the form.
       *
       * Your theme's existing AJAX ATC listener
       * will now receive this submission normally.
       */
      if (typeof form.requestSubmit === "function") {
        const submitter =
          event.submitter instanceof HTMLElement
            ? event.submitter
            : form.querySelector('[type="submit"]');

        if (
          submitter instanceof HTMLButtonElement ||
          submitter instanceof HTMLInputElement
        ) {
          form.requestSubmit(submitter);
        } else {
          form.requestSubmit();
        }
      } else {
        /*
         * Fallback for older browsers
         */
        form.dispatchEvent(
          new Event("submit", {
            bubbles: true,
            cancelable: true,
          })
        );
      }
    } catch (error) {
      console.error(
        "[Quantity Rules] Validation error:",
        error
      );

      /*
       * Fail closed.
       *
       * Better to block the ATC than accidentally
       * allow a quantity that breaks your rules.
       */
      showError(
        form,
        "Unable to verify the quantity. Please try again."
      );
    } finally {
      validatingForms.delete(form);
    }
  }

  /*
   * Capture phase is intentional.
   *
   * We need to intercept the submit BEFORE
   * the theme's AJAX cart listener processes it.
   */
  window.addEventListener(
    "submit",
    handleSubmit,
    true
  );
})();